import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 数据粒子
const DataParticle: React.FC<{
  char: string;
  x: number;
  delay: number;
  speed: number;
  opacity: number;
  color: string;
  fontSize: number;
}> = ({ char, x, delay, speed, opacity, color, fontSize }) => {
  const frame = useCurrentFrame();
  const { height } = useVideoConfig();

  const y = interpolate(
    (frame - delay) * speed,
    [0, 1],
    [-50, height + 50],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    }
  );

  const fadeOpacity = interpolate(
    frame - delay,
    [0, 10, 40, 50],
    [0, opacity, opacity, 0],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    }
  );

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        fontSize,
        color,
        opacity: fadeOpacity,
        fontFamily: 'monospace',
        fontWeight: 'bold',
        textShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
      }}
    >
      {char}
    </div>
  );
};

// 数据流组件
export const DataFlow: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // 配置参数
  const columns = 40;
  const particlesPerColumn = 8;
  const columnSpacing = width / columns;
  const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';

  // 颜色方案（渐变绿）
  const colors = [
    '#00ff41',
    '#00ff00',
    '#39ff14',
    '#7fff00',
    '#00ff7f',
  ];

  // 生成粒子
  const particles: Array<{
    char: string;
    x: number;
    delay: number;
    speed: number;
    opacity: number;
    color: string;
    fontSize: number;
  }> = [];

  for (let col = 0; col < columns; col++) {
    const baseX = col * columnSpacing + columnSpacing / 2;
    for (let i = 0; i < particlesPerColumn; i++) {
      const seed = col * 1000 + i;
      const delay = random(`delay-${seed}`) * 30 + col * 0.5;
      const speed = 0.8 + random(`speed-${seed}`) * 0.6;
      const opacity = 0.3 + random(`opacity-${seed}`) * 0.7;
      const color = colors[Math.floor(random(`color-${seed}`) * colors.length)];
      const fontSize = 14 + random(`size-${seed}`) * 18;
      const char = chars[Math.floor(random(`char-${seed}`) * chars.length)];

      particles.push({
        char,
        x: baseX,
        delay,
        speed,
        opacity,
        color,
        fontSize,
      });
    }
  }

  // 标题动画
  const titleOpacity = spring({
    frame,
    fps: 30,
    config: {
      damping: 200,
    },
  });

  const titleScale = spring({
    frame,
    fps: 30,
    config: {
      damping: 200,
      stiffness: 100,
    },
  });

  const titleY = interpolate(
    frame,
    [0, 30],
    [-50, 0],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    }
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)',
        overflow: 'hidden',
      }}
    >
      {/* 数据流粒子 */}
      {particles.map((particle, index) => (
        <DataParticle key={index} {...particle} />
      ))}

      {/* 中央标题 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) translateY(${titleY}px) scale(${titleScale})`,
          opacity: titleOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        {/* 发光背景 */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            height: 300,
            background: 'radial-gradient(ellipse, rgba(0, 255, 65, 0.15) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />

        {/* 主标题 */}
        <div
          style={{
            fontSize: 120,
            fontWeight: 'bold',
            color: '#00ff41',
            fontFamily: 'monospace',
            textShadow: `
              0 0 20px rgba(0, 255, 65, 0.8),
              0 0 40px rgba(0, 255, 65, 0.6),
              0 0 60px rgba(0, 255, 65, 0.4)
            `,
            letterSpacing: '0.1em',
          }}
        >
          DATA FLOW
        </div>

        {/* 副标题 */}
        <div
          style={{
            fontSize: 32,
            color: '#00ff00',
            fontFamily: 'monospace',
            marginTop: 20,
            textShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
            opacity: 0.8,
          }}
        >
          {'>'} SYSTEM INITIALIZING...
        </div>
      </div>

      {/* 底部装饰条 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 20,
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => {
          const barWidth = interpolate(
            frame + i * 10,
            [0, 30, 60, 90],
            [0, 100, 80, 120],
            {
              extrapolateRight: 'clamp',
            }
          );

          return (
            <div
              key={i}
              style={{
                width: barWidth,
                height: 4,
                background: `linear-gradient(90deg, transparent, ${colors[i % colors.length]}, transparent)`,
                boxShadow: `0 0 10px ${colors[i % colors.length]}`,
              }}
            />
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
